import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Component from '../../components/Component';
import {
  createAdminFanVideo,
  createAdminFanVideoMultipart,
  deleteAdminFanVideo,
  getAdminFanVideos,
  getGeneratedFanStories,
  getFanStoryFeed,
  getFanVideoApiPayload,
  isFanVideoApiSuccess,
  setAdminFanVideoEnabled,
  updateAdminFanVideo,
  updateAdminFanVideoMultipart,
  uploadAdminFanVideoAssets,
} from '../../services/fanVideoApi';
import DiscoverSections from '../DiscoverSections/DiscoverSections';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Add, Delete, Edit } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';

const VIDEO_TYPE_OPTIONS = [
  { value: 'promotional', label: 'Promotional' },
  { value: 'dugout_daily', label: 'Dugout Daily' },
  { value: 'announcement', label: "What's New / Announcement" },
];

const emptyVideoForm = {
  videoType: 'promotional',
  title: '',
  subtitle: '',
  videoUrl: '',
  thumbnailUrl: '',
  ctaText: '',
  ctaUrl: '',
  enabled: false,
  sortOrder: '0',
  startsAt: '',
  endsAt: '',
};

const GLOBAL_VIDEO_TAB_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'promotional', label: 'Promotional' },
  { value: 'dugout_daily', label: 'Dugout Daily' },
  { value: 'announcement', label: "What's New" },
];

const FAN_STORY_PAGE_SIZE = 50;

const formatDateTime = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
};

const toDateInputValue = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  const local = new Date(parsed.getTime() - parsed.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const fromDateInputValue = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
};

const isValidUrl = (value) => {
  if (!value) return true;
  try {
    const parsed = new URL(value);
    return ['http:', 'https:', 'dugout:'].includes(parsed.protocol);
  } catch {
    return false;
  }
};

const getAssetValue = (asset, camelKey, snakeKey) => asset?.[camelKey] ?? asset?.[snakeKey];
const getAssetType = (asset) => asset?.type || getAssetValue(asset, 'videoType', 'video_type') || 'promotional';
const getAssetUrl = (asset) => getAssetValue(asset, 'videoUrl', 'video_url') || '';
const getAssetEnabled = (asset) => Boolean(getAssetValue(asset, 'enabled', 'enabled'));

const normalizeVideoAssets = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.videos)) return payload.videos;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.assets)) return payload.assets;
  if (Array.isArray(payload?.fanVideos)) return payload.fanVideos;
  if (Array.isArray(payload?.rows)) return payload.rows;
  return [];
};

const normalizeFeedGroups = (payload) => {
  if (Array.isArray(payload?.videos)) return payload.videos;
  return [];
};

function FanVideoDashboard() {
  const [mainTab, setMainTab] = useState('global');
  const [globalVideoFilter, setGlobalVideoFilter] = useState('all');
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingAssets, setUploadingAssets] = useState(false);
  const [checking, setChecking] = useState(false);
  const [storiesLoading, setStoriesLoading] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [assetToDelete, setAssetToDelete] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [formData, setFormData] = useState(emptyVideoForm);
  const [videoFile, setVideoFile] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [userId, setUserId] = useState('');
  const [feedData, setFeedData] = useState(null);
  const [fanStories, setFanStories] = useState([]);
  const [fanStoriesTotal, setFanStoriesTotal] = useState(0);
  const [fanStoriesOffset, setFanStoriesOffset] = useState(0);
  const [fanStoriesUserId, setFanStoriesUserId] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleSessionExpired = useCallback(
    (res) => {
      if (String(res?.statusCode || '') === '10003') {
        showSnackbar('Session expired. Please login again.', 'error');
        setTimeout(() => {
          window.location = '/login';
        }, 1500);
        return true;
      }
      return false;
    },
    [showSnackbar]
  );

  const loadAssets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAdminFanVideos();
      if (handleSessionExpired(res)) return;
      if (!isFanVideoApiSuccess(res)) {
        setAssets([]);
        showSnackbar(res?.message || 'Failed to load fan videos', 'error');
        return;
      }
      setAssets(normalizeVideoAssets(getFanVideoApiPayload(res)));
    } finally {
      setLoading(false);
    }
  }, [handleSessionExpired, showSnackbar]);

  useEffect(() => {
    loadAssets();
  }, [loadAssets]);

  const loadFanStories = useCallback(async () => {
    setStoriesLoading(true);
    try {
      const res = await getGeneratedFanStories({
        limit: FAN_STORY_PAGE_SIZE,
        offset: fanStoriesOffset,
        userId: fanStoriesUserId.trim() || undefined,
      });
      if (handleSessionExpired(res)) return;
      if (!isFanVideoApiSuccess(res)) {
        setFanStories([]);
        setFanStoriesTotal(0);
        showSnackbar(res?.message || 'Failed to load generated fan stories', 'error');
        return;
      }
      const payload = getFanVideoApiPayload(res) || {};
      setFanStories(Array.isArray(payload.items) ? payload.items : []);
      setFanStoriesTotal(Number(payload.total || 0));
    } finally {
      setStoriesLoading(false);
    }
  }, [fanStoriesOffset, fanStoriesUserId, handleSessionExpired, showSnackbar]);

  useEffect(() => {
    if (mainTab === 'stories') {
      loadFanStories();
    }
  }, [mainTab, loadFanStories]);

  const stats = useMemo(() => {
    const promotional = assets.filter((asset) => getAssetType(asset) === 'promotional');
    const dugoutDaily = assets.filter((asset) => getAssetType(asset) === 'dugout_daily');
    const announcement = assets.filter((asset) => getAssetType(asset) === 'announcement');
    return {
      total: assets.length,
      promotionalEnabled: promotional.filter(getAssetEnabled).length,
      dugoutDailyEnabled: dugoutDaily.filter(getAssetEnabled).length,
      announcementEnabled: announcement.filter(getAssetEnabled).length,
    };
  }, [assets]);

  const feedGroups = useMemo(() => normalizeFeedGroups(feedData), [feedData]);
  const fanStoryGroup = feedGroups.find((group) => group.key === 'fan_story');
  const fanStoryAvailable = Boolean(fanStoryGroup?.enabled && fanStoryGroup?.items?.length);
  const filteredAssets = useMemo(() => {
    if (globalVideoFilter === 'all') return assets;
    return assets.filter((asset) => getAssetType(asset) === globalVideoFilter);
  }, [assets, globalVideoFilter]);

  const handleOpenCreate = () => {
    setEditingAsset(null);
    setFormData(emptyVideoForm);
    setVideoFile(null);
    setThumbnailFile(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (asset) => {
    setEditingAsset(asset);
    setVideoFile(null);
    setThumbnailFile(null);
    setFormData({
      videoType: getAssetType(asset),
      title: getAssetValue(asset, 'title', 'title') || '',
      subtitle: getAssetValue(asset, 'subtitle', 'subtitle') || '',
      videoUrl: getAssetUrl(asset),
      thumbnailUrl: getAssetValue(asset, 'thumbnailUrl', 'thumbnail_url') || '',
      ctaText: getAssetValue(asset, 'ctaText', 'cta_text') || '',
      ctaUrl: getAssetValue(asset, 'ctaUrl', 'cta_url') || '',
      enabled: getAssetEnabled(asset),
      sortOrder: String(getAssetValue(asset, 'sortOrder', 'sort_order') ?? 0),
      startsAt: toDateInputValue(getAssetValue(asset, 'startsAt', 'starts_at')),
      endsAt: toDateInputValue(getAssetValue(asset, 'endsAt', 'ends_at')),
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingAsset(null);
    setFormData(emptyVideoForm);
    setVideoFile(null);
    setThumbnailFile(null);
  };

  const updateForm = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const buildPayload = () => {
    const sortOrder = Number(formData.sortOrder || 0);
    return {
      videoType: formData.videoType,
      title: formData.title.trim(),
      subtitle: formData.subtitle.trim() || null,
      videoUrl: formData.videoUrl.trim(),
      thumbnailUrl: formData.thumbnailUrl.trim() || null,
      ctaText: formData.ctaText.trim() || null,
      ctaUrl: formData.ctaUrl.trim() || null,
      enabled: !!formData.enabled,
      sortOrder: Number.isFinite(sortOrder) ? sortOrder : 0,
      startsAt: fromDateInputValue(formData.startsAt),
      endsAt: fromDateInputValue(formData.endsAt),
    };
  };

  const buildMultipartFormData = (payload) => {
    const body = new FormData();
    body.append('videoType', payload.videoType);
    body.append('title', payload.title || '');
    body.append('subtitle', payload.subtitle || '');
    body.append('videoUrl', payload.videoUrl || '');
    body.append('thumbnailUrl', payload.thumbnailUrl || '');
    body.append('ctaText', payload.ctaText || '');
    body.append('ctaUrl', payload.ctaUrl || '');
    body.append('enabled', String(payload.enabled));
    body.append('sortOrder', String(payload.sortOrder));
    body.append('startsAt', payload.startsAt || '');
    body.append('endsAt', payload.endsAt || '');
    if (videoFile) body.append('video', videoFile);
    if (thumbnailFile) body.append('thumbnail', thumbnailFile);
    return body;
  };

  const handleSave = async () => {
    const payload = buildPayload();
    if (!payload.videoUrl && !videoFile) {
      showSnackbar('Video URL or video upload is required', 'warning');
      return;
    }
    if (payload.videoUrl && !isValidUrl(payload.videoUrl)) {
      showSnackbar('Video URL must be a valid URL', 'warning');
      return;
    }
    if (payload.thumbnailUrl && !isValidUrl(payload.thumbnailUrl)) {
      showSnackbar('Thumbnail URL must be a valid URL', 'warning');
      return;
    }
    if (payload.ctaUrl && !isValidUrl(payload.ctaUrl)) {
      showSnackbar('CTA URL must be a valid URL or deep link', 'warning');
      return;
    }
    if (payload.startsAt && payload.endsAt && new Date(payload.startsAt) > new Date(payload.endsAt)) {
      showSnackbar('Start time must be before end time', 'warning');
      return;
    }
    if (payload.enabled && payload.startsAt && new Date(payload.startsAt) > new Date()) {
      showSnackbar('This video is enabled but scheduled for the future', 'warning');
    }
    if (payload.enabled && payload.endsAt && new Date(payload.endsAt) < new Date()) {
      showSnackbar('This video is enabled but already expired', 'warning');
    }

    setSaving(true);
    try {
      const res = videoFile || thumbnailFile
        ? editingAsset
          ? await updateAdminFanVideoMultipart(editingAsset.id, buildMultipartFormData(payload))
          : await createAdminFanVideoMultipart(buildMultipartFormData(payload))
        : editingAsset
          ? await updateAdminFanVideo(editingAsset.id, payload)
          : await createAdminFanVideo(payload);
      if (handleSessionExpired(res)) return;
      if (!isFanVideoApiSuccess(res)) {
        showSnackbar(res?.message || 'Failed to save fan video', 'error');
        return;
      }
      showSnackbar(editingAsset ? 'Fan video updated' : 'Fan video created');
      handleCloseDialog();
      loadAssets();
    } finally {
      setSaving(false);
    }
  };

  const handleUploadAssetsOnly = async () => {
    if (!videoFile && !thumbnailFile) {
      showSnackbar('Choose a video or thumbnail file first', 'warning');
      return;
    }

    setUploadingAssets(true);
    try {
      const res = await uploadAdminFanVideoAssets({ videoFile, thumbnailFile });
      if (handleSessionExpired(res)) return;
      if (!isFanVideoApiSuccess(res)) {
        showSnackbar(res?.message || 'Failed to upload fan video assets', 'error');
        return;
      }
      const payload = getFanVideoApiPayload(res) || {};
      setFormData((prev) => ({
        ...prev,
        videoUrl: payload.videoUrl || prev.videoUrl,
        thumbnailUrl: payload.thumbnailUrl || prev.thumbnailUrl,
      }));
      setVideoFile(null);
      setThumbnailFile(null);
      showSnackbar('Fan video asset upload complete');
    } finally {
      setUploadingAssets(false);
    }
  };

  const handleToggleEnabled = async (asset, enabled) => {
    setTogglingId(asset.id);
    try {
      const res = await setAdminFanVideoEnabled(asset.id, enabled);
      if (handleSessionExpired(res)) return;
      if (!isFanVideoApiSuccess(res)) {
        showSnackbar(res?.message || 'Failed to update enabled status', 'error');
        return;
      }
      showSnackbar(enabled ? 'Video enabled' : 'Video disabled');
      loadAssets();
    } finally {
      setTogglingId(null);
    }
  };

  const handleOpenDelete = (asset) => {
    setAssetToDelete(asset);
  };

  const handleCloseDelete = () => {
    if (deletingId) return;
    setAssetToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!assetToDelete?.id) return;
    const id = assetToDelete.id;
    setDeletingId(id);
    try {
      const res = await deleteAdminFanVideo(id);
      if (handleSessionExpired(res)) return;
      if (!isFanVideoApiSuccess(res)) {
        showSnackbar(res?.message || 'Failed to delete fan video', 'error');
        return;
      }
      setAssets((prev) => prev.filter((asset) => asset.id !== id));
      setAssetToDelete(null);
      showSnackbar('Fan video deleted');
    } finally {
      setDeletingId(null);
    }
  };

  const handleCheckFeed = async () => {
    const trimmedUserId = userId.trim();
    if (!trimmedUserId) {
      showSnackbar('Enter a user ID to check fan story availability', 'warning');
      return;
    }

    setChecking(true);
    setFeedData(null);
    try {
      const res = await getFanStoryFeed(trimmedUserId);
      if (handleSessionExpired(res)) return;
      if (!isFanVideoApiSuccess(res)) {
        showSnackbar(res?.message || 'Failed to load fan story feed', 'error');
        return;
      }
      setFeedData(getFanVideoApiPayload(res));
      showSnackbar('Fan story feed loaded');
    } finally {
      setChecking(false);
    }
  };

  const handleApplyFanStorySearch = () => {
    if (fanStoriesOffset !== 0) {
      setFanStoriesOffset(0);
      return;
    }
    loadFanStories();
  };

  const handleViewStoryFeed = async (storyUserId) => {
    if (!storyUserId) return;
    setUserId(String(storyUserId));
    setMainTab('global');
    setChecking(true);
    setFeedData(null);
    try {
      const res = await getFanStoryFeed(storyUserId);
      if (handleSessionExpired(res)) return;
      if (!isFanVideoApiSuccess(res)) {
        showSnackbar(res?.message || 'Failed to load fan story feed', 'error');
        return;
      }
      setFeedData(getFanVideoApiPayload(res));
      showSnackbar('Fan story feed loaded');
    } finally {
      setChecking(false);
    }
  };

  return (
    <div>
      <Component>
        <div className="page-body">
          <div className="container-fluid">
            <div className="page-header">
              <div className="row">
                <div className="col-sm-6">
                  <h3>Fan Videos / Discover</h3>
                  <Typography variant="body2" color="text.secondary">
                    Manage Discover cards, available videos, and generated fan story recaps.
                  </Typography>
                </div>
              </div>
            </div>
          </div>

          <div className="container-fluid">
            <div className="row">
              <div className="col-sm-12">
                <div className="card">
                  <div className="card-body">
                    <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Button component={Link} to="/dashboard" startIcon={<ArrowBackIcon />} variant="outlined" size="small">
                        Back
                      </Button>
                      {mainTab === 'global' && (
                        <Button variant="contained" startIcon={<Add />} onClick={handleOpenCreate}>
                          Add global video
                        </Button>
                      )}
                      <Button variant="text" onClick={loadAssets} disabled={loading}>
                        Refresh
                      </Button>
                    </Box>

                    <Tabs value={mainTab} onChange={(_, value) => setMainTab(value)} sx={{ mb: 2 }}>
                      <Tab value="discover" label="Discover Sections" />
                      <Tab value="global" label="Available Videos" />
                      <Tab value="stories" label="Generated Fan Stories" />
                    </Tabs>

                    {mainTab === 'discover' ? (
                      <DiscoverSections embedded />
                    ) : mainTab === 'global' ? (
                      <>
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                          <Grid item xs={12} sm={6} md={3}>
                            <Card variant="outlined">
                              <CardContent>
                                <Typography color="text.secondary" variant="body2">
                                  User fan story
                                </Typography>
                                <Typography variant="h5" sx={{ mt: 1 }}>
                                  {feedData ? (fanStoryAvailable ? 'Available' : 'Missing') : 'Check user'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Stored S3 URL from `/fan-story/:userId`
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Card variant="outlined">
                              <CardContent>
                                <Typography color="text.secondary" variant="body2">
                                  Global assets
                                </Typography>
                                <Typography variant="h5" sx={{ mt: 1 }}>
                                  {stats.total}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Promotion, Dugout Daily, and What's New
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Card variant="outlined">
                              <CardContent>
                                <Typography color="text.secondary" variant="body2">
                                  Promotional enabled
                                </Typography>
                                <Typography variant="h5" sx={{ mt: 1 }}>
                                  {stats.promotionalEnabled}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Visible to all users
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Card variant="outlined">
                              <CardContent>
                                <Typography color="text.secondary" variant="body2">
                                  Dugout Daily enabled
                                </Typography>
                                <Typography variant="h5" sx={{ mt: 1 }}>
                                  {stats.dugoutDailyEnabled}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Visible to all users
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Card variant="outlined">
                              <CardContent>
                                <Typography color="text.secondary" variant="body2">
                                  What's New enabled
                                </Typography>
                                <Typography variant="h5" sx={{ mt: 1 }}>
                                  {stats.announcementEnabled}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  announcement videos
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        </Grid>

                        <Card variant="outlined" sx={{ mb: 3 }}>
                          <CardContent>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                              Check User Video Feed
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
                              <TextField
                                size="small"
                                label="User ID"
                                value={userId}
                                onChange={(event) => setUserId(event.target.value)}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter') handleCheckFeed();
                                }}
                                sx={{ minWidth: 220 }}
                              />
                              <Button variant="contained" onClick={handleCheckFeed} disabled={checking}>
                                {checking ? 'Checking...' : 'Check availability'}
                              </Button>
                            </Box>

                            {checking ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                <CircularProgress size={28} />
                              </Box>
                            ) : feedData ? (
                              <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                  User {feedData.userId || userId}: groups stay in the feed even when unavailable.
                                </Typography>
                                <Grid container spacing={2}>
                                  {feedGroups.map((group) => (
                                    <Grid item xs={12} md={4} key={group.key}>
                                      <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                          <Typography variant="subtitle1" fontWeight={600}>
                                            {group.key}
                                          </Typography>
                                          <Chip
                                            size="small"
                                            color={group.enabled ? 'success' : 'default'}
                                            label={group.enabled ? 'Available' : 'Unavailable'}
                                          />
                                        </Box>
                                        {(group.items || []).length === 0 ? (
                                          <Typography variant="body2" color="text.secondary">
                                            No video items in this group.
                                          </Typography>
                                        ) : (
                                          (group.items || []).map((item) => {
                                            const itemUrl = item.videoUrl || item.video_url;
                                            return (
                                              <Box key={item.id || itemUrl} sx={{ mt: 1 }}>
                                                <Typography variant="body2" fontWeight={500}>
                                                  {item.title || item.fanTypeLabel || item.type || item.id}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                  {item.durationSec ? `${item.durationSec}s` : item.source || 'video'}
                                                  {item.updatedAt ? ` | Updated ${formatDateTime(item.updatedAt)}` : ''}
                                                </Typography>
                                                {itemUrl && (
                                                  <Button
                                                    size="small"
                                                    href={itemUrl}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    endIcon={<OpenInNewIcon fontSize="small" />}
                                                    sx={{ mt: 1 }}
                                                  >
                                                    Open video
                                                  </Button>
                                                )}
                                              </Box>
                                            );
                                          })
                                        )}
                                      </Paper>
                                    </Grid>
                                  ))}
                                </Grid>
                              </Box>
                            ) : (
                              <Alert severity="info">
                                Enter a user ID to confirm whether the pre-generated IPL fan story is available and to see
                                the global videos included in the feed.
                              </Alert>
                            )}
                          </CardContent>
                        </Card>

                        <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                          <Typography variant="h6">Available Videos</Typography>
                          <TextField
                            select
                            size="small"
                            label="Type"
                            value={globalVideoFilter}
                            onChange={(event) => setGlobalVideoFilter(event.target.value)}
                            sx={{ minWidth: 180 }}
                          >
                            {GLOBAL_VIDEO_TAB_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Box>
                        {loading ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                          </Box>
                        ) : (
                          <TableContainer component={Paper}>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>ID</TableCell>
                                  <TableCell>Type</TableCell>
                                  <TableCell>Title</TableCell>
                                  <TableCell>Video</TableCell>
                                  <TableCell>Thumbnail</TableCell>
                                  <TableCell>Enabled</TableCell>
                                  <TableCell>Sort</TableCell>
                                  <TableCell>Active window</TableCell>
                                  <TableCell>Updated</TableCell>
                                  <TableCell align="right">Actions</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {filteredAssets.length === 0 ? (
                                  <TableRow>
                                    <TableCell colSpan={10} align="center">
                                      No global videos found
                                    </TableCell>
                                  </TableRow>
                                ) : (
                                  filteredAssets.map((asset) => {
                                    const videoUrl = getAssetUrl(asset);
                                    const thumbnailUrl = getAssetValue(asset, 'thumbnailUrl', 'thumbnail_url');
                                    const enabled = getAssetEnabled(asset);
                                    const startsAt = getAssetValue(asset, 'startsAt', 'starts_at');
                                    const endsAt = getAssetValue(asset, 'endsAt', 'ends_at');
                                    return (
                                      <TableRow key={asset.id} hover>
                                        <TableCell>{asset.id}</TableCell>
                                        <TableCell>
                                          <Chip
                                            size="small"
                                            label={VIDEO_TYPE_OPTIONS.find((option) => option.value === getAssetType(asset))?.label || getAssetType(asset)}
                                          />
                                        </TableCell>
                                        <TableCell>
                                          <Typography variant="body2" fontWeight={500}>
                                            {getAssetValue(asset, 'title', 'title') || '-'}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary" display="block">
                                            {getAssetValue(asset, 'subtitle', 'subtitle') || ''}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          {videoUrl ? (
                                            <Button
                                              size="small"
                                              href={videoUrl}
                                              target="_blank"
                                              rel="noreferrer"
                                              endIcon={<OpenInNewIcon fontSize="small" />}
                                            >
                                              Preview
                                            </Button>
                                          ) : (
                                            '-'
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          {thumbnailUrl ? (
                                            <img
                                              src={thumbnailUrl}
                                              alt={getAssetValue(asset, 'title', 'title') || ''}
                                              style={{ width: 54, height: 54, objectFit: 'cover', borderRadius: 6 }}
                                            />
                                          ) : (
                                            '-'
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          <Switch
                                            size="small"
                                            checked={enabled}
                                            disabled={togglingId === asset.id}
                                            onChange={(event) => handleToggleEnabled(asset, event.target.checked)}
                                          />
                                        </TableCell>
                                        <TableCell>{getAssetValue(asset, 'sortOrder', 'sort_order') ?? 0}</TableCell>
                                        <TableCell>
                                          <Typography variant="caption" display="block">
                                            Start: {formatDateTime(startsAt)}
                                          </Typography>
                                          <Typography variant="caption" display="block">
                                            End: {formatDateTime(endsAt)}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>{formatDateTime(getAssetValue(asset, 'updatedAt', 'updated_at'))}</TableCell>
                                        <TableCell align="right">
                                          <IconButton color="primary" size="small" onClick={() => handleOpenEdit(asset)}>
                                            <Edit fontSize="small" />
                                          </IconButton>
                                          <IconButton
                                            color="error"
                                            size="small"
                                            onClick={() => handleOpenDelete(asset)}
                                            disabled={deletingId === asset.id}
                                            aria-label="Delete video"
                                          >
                                            <Delete fontSize="small" />
                                          </IconButton>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })
                                )}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )}
                      </>
                    ) : (
                      <>
                        <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                          <TextField
                            size="small"
                            label="Search user ID"
                            value={fanStoriesUserId}
                            onChange={(event) => setFanStoriesUserId(event.target.value)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') handleApplyFanStorySearch();
                            }}
                            sx={{ minWidth: 220 }}
                          />
                          <Button variant="contained" onClick={handleApplyFanStorySearch} disabled={storiesLoading}>
                            Search
                          </Button>
                          <Button variant="text" onClick={loadFanStories} disabled={storiesLoading}>
                            Refresh
                          </Button>
                          <Typography variant="body2" color="text.secondary">
                            {fanStoriesTotal} generated fan story video(s)
                          </Typography>
                        </Box>

                        {storiesLoading ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                          </Box>
                        ) : (
                          <>
                            <TableContainer component={Paper}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>User ID</TableCell>
                                    <TableCell>Fan Type</TableCell>
                                    <TableCell>User Type</TableCell>
                                    <TableCell>Duration</TableCell>
                                    <TableCell>Competition</TableCell>
                                    <TableCell>Updated</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {fanStories.length === 0 ? (
                                    <TableRow>
                                      <TableCell colSpan={7} align="center">
                                        No generated fan story videos found
                                      </TableCell>
                                    </TableRow>
                                  ) : (
                                    fanStories.map((story) => (
                                      <TableRow key={`${story.userId}-${story.competitionCid}`} hover>
                                        <TableCell>{story.userId}</TableCell>
                                        <TableCell>{story.fanTypeLabel || '-'}</TableCell>
                                        <TableCell>{story.userType || '-'}</TableCell>
                                        <TableCell>{story.durationSec ? `${story.durationSec}s` : '-'}</TableCell>
                                        <TableCell>{story.competitionCid || '-'}</TableCell>
                                        <TableCell>{formatDateTime(story.updatedAt)}</TableCell>
                                        <TableCell align="right">
                                          <Button
                                            size="small"
                                            href={story.videoUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            endIcon={<OpenInNewIcon fontSize="small" />}
                                            disabled={!story.videoUrl}
                                          >
                                            Preview
                                          </Button>
                                          <Button
                                            size="small"
                                            onClick={() => handleViewStoryFeed(story.userId)}
                                          >
                                            View feed
                                          </Button>
                                        </TableCell>
                                      </TableRow>
                                    ))
                                  )}
                                </TableBody>
                              </Table>
                            </TableContainer>
                            <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
                              <Button
                                disabled={fanStoriesOffset === 0 || storiesLoading}
                                onClick={() => setFanStoriesOffset((offset) => Math.max(0, offset - FAN_STORY_PAGE_SIZE))}
                              >
                                Previous
                              </Button>
                              <Button
                                disabled={fanStoriesOffset + FAN_STORY_PAGE_SIZE >= fanStoriesTotal || storiesLoading}
                                onClick={() => setFanStoriesOffset((offset) => offset + FAN_STORY_PAGE_SIZE)}
                              >
                                Next
                              </Button>
                              <Typography variant="body2" color="text.secondary">
                                Offset {fanStoriesOffset}
                              </Typography>
                            </Box>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Dialog open={Boolean(assetToDelete)} onClose={handleCloseDelete} maxWidth="sm" fullWidth>
          <DialogTitle>Delete video?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Delete “{getAssetValue(assetToDelete, 'title', 'title') || `video #${assetToDelete?.id}`}”? This removes it
              from Available Videos and from any Discover cards that use it. This cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDelete} disabled={Boolean(deletingId)}>
              Cancel
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={handleConfirmDelete}
              disabled={Boolean(deletingId)}
            >
              {deletingId ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>{editingAsset ? `Edit Fan Video #${editingAsset.id}` : 'Add Global Fan Video'}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ pt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Video type"
                  value={formData.videoType}
                  onChange={(event) => updateForm('videoType', event.target.value)}
                >
                  {VIDEO_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Sort order"
                  type="number"
                  value={formData.sortOrder}
                  onChange={(event) => updateForm('sortOrder', event.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Title"
                  value={formData.title}
                  onChange={(event) => updateForm('title', event.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Subtitle"
                  value={formData.subtitle}
                  onChange={(event) => updateForm('subtitle', event.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required={!videoFile}
                  label="Video URL"
                  value={formData.videoUrl}
                  onChange={(event) => updateForm('videoUrl', event.target.value)}
                  helperText="Use an existing URL or upload a video file below. Uploaded video wins on save."
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Button variant="outlined" component="label">
                    Choose video
                    <input
                      hidden
                      accept="video/*"
                      type="file"
                      onChange={(event) => setVideoFile(event.target.files?.[0] || null)}
                    />
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    {videoFile ? videoFile.name : 'No video selected'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Thumbnail URL"
                  value={formData.thumbnailUrl}
                  onChange={(event) => updateForm('thumbnailUrl', event.target.value)}
                  helperText="Use an existing image URL or upload a thumbnail below."
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Button variant="outlined" component="label">
                    Choose thumbnail
                    <input
                      hidden
                      accept="image/*"
                      type="file"
                      onChange={(event) => setThumbnailFile(event.target.files?.[0] || null)}
                    />
                  </Button>
                  <Button
                    variant="text"
                    onClick={handleUploadAssetsOnly}
                    disabled={(!videoFile && !thumbnailFile) || uploadingAssets}
                  >
                    {uploadingAssets ? 'Uploading...' : 'Upload files only'}
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    {thumbnailFile ? thumbnailFile.name : 'No thumbnail selected'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="CTA text"
                  value={formData.ctaText}
                  onChange={(event) => updateForm('ctaText', event.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="CTA URL"
                  value={formData.ctaUrl}
                  onChange={(event) => updateForm('ctaUrl', event.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Starts at"
                  type="datetime-local"
                  value={formData.startsAt}
                  onChange={(event) => updateForm('startsAt', event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  helperText="Prefer leaving empty — schedule the Discover tile instead"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Ends at"
                  type="datetime-local"
                  value={formData.endsAt}
                  onChange={(event) => updateForm('endsAt', event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  helperText="Expired windows are cleared when re-assigned with makeLive"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.enabled}
                      onChange={(event) => updateForm('enabled', event.target.checked)}
                    />
                  }
                  label="Enabled for all users"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button variant="contained" onClick={handleSave} disabled={saving || uploadingAssets}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Component>
    </div>
  );
}

export default FanVideoDashboard;
